import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { TaxRegimeEnum } from "@repo/graphql/gql/graphql";
import { Check, CheckCheck } from "lucide-react-native";
import { useController, useFormState, useWatch } from "react-hook-form";

import RegimeCard from "./RegimeCard";
import type { TaxProfileFormState } from "./useTaxProfileForm";
import type {
  RegimeOption,
  TaxProfileFieldData,
  TaxProfilePerspective,
} from "./types";

type Props = {
  field: TaxProfileFieldData;
  form: TaxProfileFormState;
  perspective: TaxProfilePerspective;
};

const cardCopy = (regime: RegimeOption, perspective: TaxProfilePerspective) =>
  perspective === "payer"
    ? { label: regime.payerLabel, description: regime.payerDescription }
    : { label: regime.label, description: regime.description };

export default function RegimeField({ field, form, perspective }: Props) {
  const { formProps, regimes, selectedRegime } = form;
  const { control } = formProps;
  const selectedCode = useWatch({ control, name: "taxRegime" });
  const { errors } = useFormState({ control, name: "taxRegime" });
  const {
    field: acknowledgedField,
    fieldState: { error: acknowledgedError },
  } = useController({ control, name: "acknowledged" });

  // Only a creator reads the regime's highlights and ticks its acknowledgment;
  // a payer confirms its data with the form's own checkbox.
  const highlights =
    perspective === "creator" ? (selectedRegime?.highlights ?? []) : [];

  const selectRegime = (code: TaxRegimeEnum) => {
    if (code === selectedCode) {
      return;
    }
    formProps.setValue("taxRegime", code, { shouldValidate: true });
    if (perspective === "creator") {
      formProps.setValue("acknowledged", false, { shouldValidate: true });
    }
  };

  return (
    <VStack space="sm">
      <Text
        size="2xs"
        bold
        className="uppercase text-typography-500 tracking-wide"
      >
        {field.label}
      </Text>
      <VStack space="sm">
        {regimes.map((regime) => (
          <RegimeCard
            key={regime.code}
            regime={regime}
            {...cardCopy(regime, perspective)}
            selected={selectedCode === regime.code}
            onSelect={() => selectRegime(regime.code)}
          />
        ))}
      </VStack>
      {errors.taxRegime?.message ? (
        <Text size="xs" className="text-error-600">
          {errors.taxRegime.message}
        </Text>
      ) : null}

      {highlights.length > 0 && selectedRegime ? (
        <VStack
          space="sm"
          className="rounded-2xl border border-brand-100 bg-brand-50 p-4"
        >
          {highlights.map((highlight) => (
            <HStack key={highlight} space="sm" className="items-start">
              <Icon
                as={CheckCheck}
                size="sm"
                className="text-brand-600 mt-[2px]"
              />
              <Text size="sm" className="flex-1 text-typography-700">
                {highlight}
              </Text>
            </HStack>
          ))}

          {/* A plain Pressable row rather than the Gluestack Checkbox: on
              native the Gluestack root never registered the tap here, so
              the acknowledgment could not be ticked. Same pattern as the
              accept-flow checkbox and the regime cards above. */}
          <Pressable
            onPress={() => acknowledgedField.onChange(!acknowledgedField.value)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: !!acknowledgedField.value }}
          >
            {(pressableProps) => (
              <HStack
                space="sm"
                className={`items-start ${
                  pressableProps.pressed ? "opacity-80" : ""
                }`}
              >
                <Box
                  className={`rounded-md items-center justify-center w-[22px] h-[22px] mt-[1px] ${
                    acknowledgedField.value
                      ? "bg-brand-400"
                      : "bg-background-0 border-[1.5px] border-outline-300"
                  }`}
                >
                  {acknowledgedField.value ? (
                    <Icon
                      as={Check}
                      size="2xs"
                      className="text-typography-white"
                    />
                  ) : null}
                </Box>
                <Text size="sm" className="flex-1 text-typography-700">
                  {selectedRegime.acknowledgmentLabel}
                </Text>
              </HStack>
            )}
          </Pressable>
          {acknowledgedError ? (
            <Text size="xs" className="text-error-600">
              {acknowledgedError.message}
            </Text>
          ) : null}
        </VStack>
      ) : null}
    </VStack>
  );
}
